import React from 'react';
import Video from '../wrapper/Video';

// Only treat a play() rejection as slide-fatal if the element has
// exhausted every <source> (NETWORK_NO_SOURCE = 3). With multi-source
// <video>, a play() promise can reject mid-walk (e.g. webm 404) while
// the browser is still about to try the next source — we don't want
// that transient rejection to push the slide into Brood's backoff list.
//
// v = this.videoRef.current
const isFatalPlayError = (v) => {
	if (!v) return true; // unmounted — treat as fatal so we don't dangle
	// NETWORK_NO_SOURCE = all <source> children exhausted.
	if (v.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) return true;
	// v.error populated => MediaError fired (decode/network/etc.) even if
	// networkState is still mid-walk. Closes the silent-stuck race where
	// play() rejects but the <video> 'error' event won't fire later.
	if (v.error) return true;
	return false;
};

class BroodVideoSlide extends React.Component {
	constructor(props) {
		super(props);

		// this.state = {};

		this.videoPlayedFully = false;
		this.videoRef = React.createRef();
		this.playStartWatchdog = null;
		this.firstFrameSeen = false;
	}

	// Fires if the video hasn't shown signs of life within PLAY_START_WATCHDOG_MS
	// after play() resolved. Catches broken/stalled assets that don't fire 'error'
	// (slow buffer never resolves, 0fps decode, etc.) so we don't sit on a grey
	// screen for the full slide duration before advancing.
	_startPlayWatchdog() {
		if (this.firstFrameSeen) return;
		clearTimeout(this.playStartWatchdog);
		this.playStartWatchdog = setTimeout(() => {
			const v = this.videoRef.current;
			if (!v) return;
			if (v.currentTime > 0.1 && !v.paused) {
				this.firstFrameSeen = true;
				return;
			}
			const { slideshow, slide, curr } = this.props;
			if (!curr) return; // slide already advanced; unmount will clear us

			// "Still loading" check: distinguish a slow-but-healthy multi-source
			// startup (webm 404 → mp4 fallback, big file buffering) from a real
			// stall. If the browser hasn't errored, hasn't exhausted sources, and
			// hasn't loaded enough data to render a frame yet, give it one more
			// 4s window before escalating. Bounded to one retry to avoid an
			// infinite "loading" stall on a truly broken element.
			const NO_SOURCE = HTMLMediaElement.NETWORK_NO_SOURCE; // 3
			const HAVE_CURRENT_DATA = 2;
			const stillLoading = !v.error
				&& v.networkState !== NO_SOURCE
				&& v.readyState < HAVE_CURRENT_DATA;
			if (stillLoading && (this._watchdogRetries || 0) < 1) {
				this._watchdogRetries = (this._watchdogRetries || 0) + 1;
				console.log('BROOD::VIDEO::PLAY_WATCHDOG_EXTEND — still loading, granting +4s:', slide.slideID);
				this._startPlayWatchdog();
				return;
			}

			console.warn('BROOD::VIDEO::PLAY_WATCHDOG_TIMEOUT — no playback progress:', slide.slideID);
			// Intentionally NOT gated on errHandled: an earlier onVideoError /
			// _play().catch may have set it true and called slideshow.next, but
			// the slide is clearly still curr → that escalation didn't advance us.
			// Reset and re-escalate. If unmount lands before the next slideshow.next
			// processes, Brood.next's NEXT_NOT_CURR guard catches it.
			this.errHandled = true;
			slideshow.next(slide, curr, {
				error: new Error('play-start-watchdog: no playback progress')
			});
		}, 4000);
	}

	_clearPlayWatchdog() {
		clearTimeout(this.playStartWatchdog);
		this.playStartWatchdog = null;
	}

	componentDidMount() {
		const {
			curr,
			slide,
			slideshow
		} = this.props;

		// block duplicate error/slideshow.next() calls
		// due to video play promise errors catching and
		// video onerror event firing at the same time
		this.errHandled = false;

		if (curr) {
			this.play();
		}
	}

	// Brood unmounted, so clean up everything!
	componentWillUnmount() {
		// const {
		// 	slideshow
		// } = this.props;

		// if (slideshow.state.slides.length < 3) {
		// 	// don't release video from memory
		// 	// if there are only 2 slides
		// 	return;
		// }

		this._clearPlayWatchdog();
		this.unload();
	}

	componentDidUpdate(prevProps) {
		const {
			status,
			transitionDuration,
			slide,
			curr,
			next,
			done
		} = this.props;

		if (prevProps.status.playing !== status.playing) {
			if (status.playing && curr) {
				this.play();
			} else {
				this.pause();
			}
		}

		if (prevProps.curr !== curr) {
			if (curr) {
				// console.log('VIDEO::CURR::PLAY:', slide.slideID, this.props);

				this.play();
			}

			if (!curr) {
				// console.log('VIDEO::CURR::PAUSE:, slide.slideID, this.props);

				this.pause();
				// Don't reset currentTime immediately - let it stay on last frame during transition
				// The video will be reset when it becomes 'done' or when it's loaded again
			}
		}

		if (prevProps.next !== next) {
			if (next) {
				// preload video when it is the next slide
				// if we have only 2x slides the slides will
				// swap between curr and next, so we need to
				// make sure if props.done is never triggered
				// and the video is never unloaded from memory
				// that we don't preload the video again
				if (!this.loaded()) {
					console.log('BROOD::VIDEO::LOAD:', slide.slideID, this.props);

					this.load();
				}
			}
		}

		if (prevProps.done !== done) {
			if (done) {
				console.log('BROOD::VIDEO::DONE:', slide.slideID, this.props);

				clearTimeout(this.unloadTimer);

				// don't release video from memory until the transition is complete
				this.unloadTimer = setTimeout(() => {
					this.unload();
				}, transitionDuration); // matches CSS transition duration
			}
		}
	}

	load(cb = () => { }) {
		// Re-loading means we need to re-prove signs of life on next play().
		this.firstFrameSeen = false;
		this._watchdogRetries = 0;
		this._clearPlayWatchdog();
		if (this.videoRef.current) {
			// Reset video to start when loading
			this.videoRef.current.currentTime = 0;
			// Clear direct src so browser re-evaluates <source> children (webm first, then original)
			this.videoRef.current.removeAttribute('src');
			this.videoRef.current.load();
		}

		if (cb && typeof cb === 'function') {
			return cb();
		}
	}

	unload() {
		this._clearPlayWatchdog();
		this.errHandled = true;

		if (!this.videoPlayedFully) {
			return;
		}

		if (this.videoRef.current) {
			// Don't unload if video is still playing (currentTime > 0 and not paused)
			if (this.videoRef.current.currentTime > 0 && !this.videoRef.current.paused) {
				return;
			}

			this.pause();
			// Reset to beginning before clearing src
			this.videoRef.current.currentTime = 0;
			this.videoRef.current.src = '';
		}
	}

	loaded() {
		const v = this.videoRef.current;
		if (!v) {
			return false;
		}
		// NETWORK_NO_SOURCE means the browser tried every <source> child and
		// none were playable. currentSrc still points at the last-attempted URL,
		// but the element is effectively dead. Treat as not-loaded so play()
		// takes the load(_play) path and runs resource selection from scratch.
		// Common cause: preload (preload="auto" on next slide) raced with the
		// previous slide's failing loads and got a transient "no source" verdict
		// that doesn't reflect the actual asset availability.
		if (v.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) {
			return false;
		}
		// With <source> children, video.src stays '' and the resolved URL lives on currentSrc.
		// Fall back to src for the legacy direct-src case. Empty => not loaded
		// (covers post-unload() state where src was cleared).
		const url = v.currentSrc || v.src;
		if (!url) {
			return false;
		}
		return true;
	}

	_play() {
		const {
			slideshow,
			slide,
			curr
		} = this.props;

		const {
			data
		} = slide;

		if (this.videoRef.current) {
			let muted = true;
			if (slide.allowAudio) {
				muted = false;
			}
			this.videoRef.current.defaultMuted = true;
			this.videoRef.current.muted = muted;

			// Arm BEFORE calling play() so the watchdog also covers transient
			// play()-promise rejections that don't escalate (e.g. multi-source
			// mid-walk rejection where networkState !== NETWORK_NO_SOURCE — see
			// _play().catch). Otherwise .then() never fires and we'd sit grey
			// forever on those.
			this._startPlayWatchdog();

			this.videoRef.current.play()
				.then(() => {
					slideshow.broodProofOfPlayStart(slide);
					console.log('BROOD::VIDEO::PLAY:', this.props);
				})
				.catch((err) => {
					// Chrome/browsers block unmuted autoplay without user interaction.
					// Retry muted so the video still plays instead of being skipped.
					if (!muted && this.videoRef.current) {
						console.warn('BROOD::VIDEO::AUTOPLAY_BLOCKED — retrying muted:', slide.slideID);
						this.videoRef.current.muted = true;

						this.videoRef.current.play()
							.then(() => {
								slideshow.broodProofOfPlayStart(slide);
								this._startPlayWatchdog();
								console.log('BROOD::VIDEO::PLAY_MUTED:', slide.slideID);
							})
							.catch((retryErr) => {
								console.error('BROOD::VIDEO::ERROR_play_muted:', retryErr);

								if (!isFatalPlayError(this.videoRef.current)) {
									console.log('BROOD::VIDEO::PLAY_MUTED_REJECTED_BUT_SOURCES_REMAIN — letting onError handle:', slide.slideID);
									return;
								}

								if (!this.errHandled) {
									this.errHandled = true;

									slideshow.next(slide, curr, {
										error: retryErr
									});
								}
							});

						return;
					}

					console.error('BROOD::VIDEO::ERROR_play:', err);

					if (!isFatalPlayError(this.videoRef.current)) {
						console.log('BROOD::VIDEO::PLAY_REJECTED_BUT_SOURCES_REMAIN — letting onError handle:', slide.slideID);
						return;
					}

					console.log('BROOD::VIDEO::NEXT::ERROR:', err);

					if (!this.errHandled) {
						this.errHandled = true;

						slideshow.next(slide, curr, {
							error: err
						});
					}
				});
		}
	}

	play() {
		const {
			slideshow,
			slide
		} = this.props;

		this.errHandled = false;
		// Re-prove signs of life on every fresh play() — covers the case where
		// loaded() short-circuits load() (e.g. replay-without-reload on a 2-slide
		// loop) and would otherwise skip the watchdog due to a stale firstFrameSeen.
		this.firstFrameSeen = false;
		this._watchdogRetries = 0;

		if (this.loaded()) {
			// Reset video to start if it's at the end
			if (this.videoRef.current && this.videoRef.current.ended) {
				this.videoRef.current.currentTime = 0;
			}
			this._play();
		} else {
			this.load(() => {
				this._play();
			});
		}
	}

	pause() {
		// Pausing the slideshow shouldn't trigger the watchdog. It will re-arm
		// on resume via play() → _play() → .then() → _startPlayWatchdog().
		this._clearPlayWatchdog();
		if (this.videoRef.current) {
			this.videoRef.current.pause();
			// this.videoRef.current.currentTime = 0;
		}
	}

	onTimeUpdate() {
		const {
			slideshow
		} = this.props;

		const video = this.videoRef.current;

		// First real timeupdate past t=0 means playback is healthy — clear the
		// play-start watchdog so it doesn't fire mid-playback.
		if (!this.firstFrameSeen && video && video.currentTime > 0.1) {
			this.firstFrameSeen = true;
			this._clearPlayWatchdog();
		}

		if (video.loop) { // && video.currentTime >= video.duration - 0.5) {
			if (slideshow.hasNextSlide()) {
				video.loop = false;
			}
		}
	}

	onVideoError(err) {
		const {
			slideshow,
			slide,
			curr
		} = this.props;

		// NOTE: intentionally not clearing the play-start watchdog here. If our
		// slideshow.next call below succeeds, the component unmounts and the
		// watchdog is cleared by componentWillUnmount. If it doesn't (e.g.
		// mid-transition race that swallows our next() call), the watchdog
		// remains armed as a backstop and fires at T+4s to retry.

		// don't error out due to this.unload()
		if (!this.loaded()) {
			return;
		}

		console.log('BROOD::VIDEO::NEXT::ERROR:', err);

		// onError fired but sources remain (e.g. a webm <source> 404 surfaced
		// on the <video> element before the mp4 was tried). Don't escalate —
		// the browser will still walk the next <source>, and the play-start
		// watchdog (intentionally left armed in this handler) is our backstop
		// if the mp4 also stalls.
		if (!isFatalPlayError(this.videoRef.current)) {
			console.log('BROOD::VIDEO::ONERROR_BUT_SOURCES_REMAIN — letting browser continue source walk:', slide.slideID);
			return;
		}

		if (!this.errHandled) {
			this.errHandled = true;

			slideshow.next(slide, curr, {
				error: err
			});
		}
	}

	onVideoEnded() {
		const {
			slideshow,
			slide,
			curr
		} = this.props;

		this._clearPlayWatchdog();
		this.videoPlayedFully = true;

		if (slideshow.hasNextSlide()) {
			console.log('BROOD::VIDEO::NEXT');
			slideshow.next(slide, curr, {});
		}
	}

	render() {
		const {
			slideshow,
			slide,
			curr,
			next,
			className: slideClassName
		} = this.props;

		const {
			data
		} = slide;

		let preload = 'none';
		if (curr || next) {
			preload = 'auto';
		}

		if (!this.videoPlayedFully) {
			preload = 'auto';
		}

		let loop = false;
		if (!slideshow.hasNextSlide()) {
			loop = true;
		}

		return (
			<Video
				className={slideClassName}
				style={this.props.style || {}}
				ref={this.videoRef}
				data-limit-skip={slideshow.skipper(slide)}
				data-desc={slide.description}
				// crossOrigin='anonymous'
				preload={preload}
				src={data.src}
				convertFormat={data.convertFormat}
				onError={this.onVideoError.bind(this)}
				onTimeUpdate={this.onTimeUpdate.bind(this)}
				onEnded={this.onVideoEnded.bind(this)}
				loop={loop}
			// controls={true}
			/>
		);
	}
}

export default BroodVideoSlide; 